'use client';
import { useEffect, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { ComparisonChartCard } from '../../Cards/ChartCards/ComparisonChartCard';
import { CustomTooltip } from '../shared/CustomTooltip/CustomTooltip';
import styles from './ComparisonLineChart.module.css';
import { SkeletonBox } from '@practium/ui';

export interface ComparisonLineChartProps {
	title: string;
	mainValue?: string;
	trendValue?: string;
	trendLabel?: string;
	trendDirection?: 'up' | 'down';
	footerText?: string;
	buttonText: string;
	onButtonClick: () => void;
	data: object[];
	xAxisKey: string;
	showAllXAxisTicks?: boolean;
	xAxisTickFormatter?: (value: string) => string;
	line1Key: string;
	line1Label: string;
	line2Key: string;
	line2Label: string;
	tooltipTitle: string;
	emptyMessage?: string;
	isLoading?: boolean;
}

export const ComparisonLineChart = ({
	title,
	mainValue,
	trendValue,
	trendLabel,
	trendDirection,
	footerText,
	onButtonClick,
	buttonText,
	data,
	xAxisKey,
	showAllXAxisTicks = false,
	xAxisTickFormatter,
	line1Key,
	line1Label,
	line2Key,
	line2Label,
	tooltipTitle,
	emptyMessage,
	isLoading,
}: ComparisonLineChartProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [chartWidth, setChartWidth] = useState(0);
	const isEmpty = !data || data.length === 0;

	useEffect(() => {
		if (containerRef.current) {
			setChartWidth(containerRef.current.offsetWidth);
		}

		const handleResize = () => {
			if (containerRef.current) {
				setChartWidth(containerRef.current.offsetWidth);
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isLoading, isEmpty]);

	return (
		<ComparisonChartCard
			title={title}
			buttonText={buttonText}
			onButtonClick={onButtonClick}
			mainValue={mainValue}
			trendValue={trendValue}
			trendLabel={trendLabel}
			trendDirection={trendDirection}
			footerText={footerText}
			isEmpty={isEmpty}
			emptyMessage={emptyMessage}
			isLoading={isLoading}
			skeleton={<SkeletonBox width="100%" height="12.5rem" />}
			legend={
				<>
					<div className={styles.legendItem}>
						<div className={styles.legendDotPrimary} />
						<span className={styles.legendText}>{line1Label}</span>
					</div>
					<div className={styles.legendItem}>
						<div className={styles.legendDotSecondary} />
						<span className={styles.legendText}>{line2Label}</span>
					</div>
				</>
			}
		>
			<div
				ref={containerRef}
				style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
			>
				<ResponsiveContainer width="100%" height="100%">
					<LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-line)" vertical={false} />
						<XAxis
							dataKey={xAxisKey}
							interval={showAllXAxisTicks ? 0 : undefined}
							tickFormatter={xAxisTickFormatter}
							tick={{ fill: 'var(--text-secondary)', fontSize: '0.75rem' }}
							axisLine={false}
							tickLine={false}
							padding={{ left: 10, right: 10 }}
						/>
						<Tooltip
							content={(props: any) => (
								<CustomTooltip
									{...props}
									subtitle={tooltipTitle}
									variant="line"
									chartWidth={chartWidth}
								/>
							)}
							cursor={false}
							wrapperStyle={{ outline: 'none', zIndex: 1000 }}
						/>
						<Line
							type="linear"
							dataKey={line1Key}
							name={line1Label}
							stroke="var(--chart-line-primary)"
							strokeWidth={4}
							dot={false}
							activeDot={{ r: 6, strokeWidth: 0 }}
						/>
						<Line
							type="linear"
							dataKey={line2Key}
							name={line2Label}
							stroke="var(--chart-line-secondary)"
							strokeWidth={4}
							dot={false}
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</ComparisonChartCard>
	);
};
